import {
	Camera,
	Color,
	Mesh,
	MeshStandardMaterial,
	Raycaster,
	RingGeometry,
	Scene,
	SphereGeometry,
	Vector2,
	Vector3
} from 'three';

import type { BrightnessControlConfig } from './brightness-control.types';
import { BRIGHTNESS_CONTROL_DEFAULTS } from './brightness-control.types';
import type { LightingManager } from '../lighting-manager.class';

/**
 * BrightnessControl renders a draggable sphere in the 3D scene that allows
 * users to control the overall scene brightness by dragging vertically.
 *
 * Dragging up increases brightness, dragging down decreases it.
 * The sphere's emissive glow and a surrounding ring indicator reflect
 * the current brightness level.
 *
 * Uses the same brightness-scaling approach as applyCustomSkyBrightnessCfn
 * so all light sources (ambient, directional, hemisphere, IBL, background)
 * are adjusted proportionally.
 */
export class BrightnessControl {
	#scene: Scene;
	#camera: Camera;
	#domElement: HTMLElement;
	#lightingManager: LightingManager;
	#config: Required<BrightnessControlConfig>;

	#sphere: Mesh | null = null;
	#ring: Mesh | null = null;
	#raycaster: Raycaster;
	#pointer: Vector2;

	#isVisible = false;
	#isDragging = false;
	#dragStartY = 0;
	#brightness = 1.0;
	#startBrightness = 1.0;

	/** Callback to disable camera controls during drag */
	#onInteractionStart: (() => void) | null = null;
	/** Callback to re-enable camera controls after drag */
	#onInteractionEnd: (() => void) | null = null;

	constructor (config: BrightnessControlConfig) {
		const {
			scene,
			camera,
			domElement,
			lightingManager,
			position,
			sphereRadius,
			sphereColor,
			dragSensitivity,
			initialBrightness
		} = {
			...BRIGHTNESS_CONTROL_DEFAULTS,
			...config,
			// Ensure required fields from config always win
			scene: config.scene,
			camera: config.camera,
			domElement: config.domElement,
			lightingManager: config.lightingManager
		};

		this.#scene = scene;
		this.#camera = camera;
		this.#domElement = domElement;
		this.#lightingManager = lightingManager;
		this.#config = {
			scene,
			camera,
			domElement,
			lightingManager,
			position: position ?? BRIGHTNESS_CONTROL_DEFAULTS.position!,
			sphereRadius: sphereRadius ?? BRIGHTNESS_CONTROL_DEFAULTS.sphereRadius!,
			sphereColor: sphereColor ?? BRIGHTNESS_CONTROL_DEFAULTS.sphereColor!,
			dragSensitivity: dragSensitivity ?? BRIGHTNESS_CONTROL_DEFAULTS.dragSensitivity!,
			initialBrightness: initialBrightness ?? BRIGHTNESS_CONTROL_DEFAULTS.initialBrightness!
		};

		this.#brightness = this.#config.initialBrightness;
		this.#raycaster = new Raycaster();
		this.#pointer = new Vector2();

		this.createMeshes();
		this.bindEvents();
	}

	// ── Public API ──────────────────────────────────────────────────────

	/**
	 * Current brightness value (0-1)
	 */
	get brightness (): number {
		return this.#brightness;
	}

	/**
	 * Set brightness programmatically
	 */
	set brightness (value: number) {
		this.#brightness = Math.max(0, Math.min(1, value));
		this.applyBrightness();
		this.updateVisuals();
	}

	/**
	 * Whether the brightness control is currently visible
	 */
	get visible (): boolean {
		return this.#isVisible;
	}

	/**
	 * Show the brightness control sphere
	 */
	show (): void {
		if (this.#sphere) {
			this.#sphere.visible = true;
		}
		if (this.#ring) {
			this.#ring.visible = true;
		}
		this.#isVisible = true;
	}

	/**
	 * Hide the brightness control sphere
	 */
	hide (): void {
		if (this.#sphere) {
			this.#sphere.visible = false;
		}
		if (this.#ring) {
			this.#ring.visible = false;
		}
		this.#isVisible = false;
		this.cancelDrag();
	}

	/**
	 * Toggle visibility
	 */
	toggle (): void {
		if (this.#isVisible) {
			this.hide();
		} else {
			this.show();
		}
	}

	/**
	 * Update the world-space position of the brightness control
	 */
	setPosition (position: { x: number; y: number; z: number }): void {
		const { x, y, z } = position;
		if (this.#sphere) {
			this.#sphere.position.set(x, y, z);
		}
		if (this.#ring) {
			this.#ring.position.set(x, y, z);
		}
	}

	/**
	 * Set callback for when drag interaction starts
	 */
	setOnInteractionStart (callback: (() => void) | null): void {
		this.#onInteractionStart = callback;
	}

	/**
	 * Set callback for when drag interaction ends
	 */
	setOnInteractionEnd (callback: (() => void) | null): void {
		this.#onInteractionEnd = callback;
	}

	/**
	 * Whether a drag is currently in progress
	 */
	get isDragging (): boolean {
		return this.#isDragging;
	}

	/**
	 * Dispose all resources
	 */
	dispose (): void {
		this.unbindEvents();
		this.cancelDrag();

		if (this.#sphere) {
			this.#scene.remove(this.#sphere);
			(this.#sphere.material as MeshStandardMaterial).dispose();
			this.#sphere.geometry.dispose();
			this.#sphere = null;
		}

		if (this.#ring) {
			this.#scene.remove(this.#ring);
			(this.#ring.material as MeshStandardMaterial).dispose();
			this.#ring.geometry.dispose();
			this.#ring = null;
		}
	}

	// ── Internal ────────────────────────────────────────────────────────

	/**
	 * Create the sphere and ring indicator meshes
	 */
	private createMeshes (): void {
		const { position, sphereRadius, sphereColor } = this.#config;

		// Main brightness sphere
		const sphereGeometry = new SphereGeometry(sphereRadius, 32, 32);
		const sphereMaterial = new MeshStandardMaterial({
			color: sphereColor,
			emissive: new Color(sphereColor),
			emissiveIntensity: this.#brightness * 0.8,
			metalness: 0.3,
			roughness: 0.4,
			transparent: true,
			opacity: 0.9,
			depthTest: false
		});

		this.#sphere = new Mesh(sphereGeometry, sphereMaterial);
		this.#sphere.position.set(position.x, position.y, position.z);
		this.#sphere.userData = { type: 'brightness-control', isBrightnessControl: true };
		this.#sphere.renderOrder = 999;
		this.#sphere.visible = false;
		this.#scene.add(this.#sphere);

		// Surrounding ring indicator showing brightness level
		const ringGeometry = new RingGeometry(
			sphereRadius * 1.3,
			sphereRadius * 1.5,
			32
		);
		const ringMaterial = new MeshStandardMaterial({
			color: sphereColor,
			emissive: new Color(sphereColor),
			emissiveIntensity: this.#brightness * 0.6,
			transparent: true,
			opacity: 0.5,
			side: 2, // DoubleSide
			depthTest: false
		});

		this.#ring = new Mesh(ringGeometry, ringMaterial);
		this.#ring.position.set(position.x, position.y, position.z);
		this.#ring.userData = { type: 'brightness-control-ring' };
		this.#ring.renderOrder = 998;
		this.#ring.visible = false;
		this.#scene.add(this.#ring);

		this.updateVisuals();
	}

	/**
	 * Update sphere and ring visuals to reflect current brightness
	 */
	private updateVisuals (): void {
		if (this.#sphere) {
			const material = this.#sphere.material as MeshStandardMaterial;
			material.emissiveIntensity = this.#brightness * 0.8;
			material.opacity = 0.5 + this.#brightness * 0.45;

			// Shift color from dim grey-blue at low brightness to bright yellow-white at high brightness
			const dimColor = new Color(0x334455);
			const brightColor = new Color(this.#config.sphereColor);
			const blended = new Color().lerpColors(dimColor, brightColor, this.#brightness);
			material.color.copy(blended);
			material.emissive.copy(blended);
		}

		if (this.#ring) {
			const ringMaterial = this.#ring.material as MeshStandardMaterial;
			ringMaterial.emissiveIntensity = this.#brightness * 0.6;
			ringMaterial.opacity = 0.2 + this.#brightness * 0.5;

			// Make ring always face the camera
			this.#ring.lookAt(this.#camera.position);
		}
	}

	/**
	 * Apply the current brightness value to all scene lights using the same
	 * curve as applyCustomSkyBrightnessCfn
	 */
	private applyBrightness (): void {
		this.#lightingManager.applyCustomSkyBrightness(this.#brightness);
	}

	/**
	 * Update pointer from client coordinates
	 */
	private updatePointer (clientX: number, clientY: number): void {
		const rect = this.#domElement.getBoundingClientRect();
		this.#pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
		this.#pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
	}

	/**
	 * Check if the pointer hits the brightness sphere
	 */
	private checkHit (): boolean {
		if (!this.#sphere || !this.#isVisible) return false;

		this.#raycaster.setFromCamera(this.#pointer, this.#camera);
		const intersects = this.#raycaster.intersectObject(this.#sphere, false);
		return intersects.length > 0;
	}

	/**
	 * Start a drag interaction
	 */
	private startDrag (clientY: number): void {
		this.#isDragging = true;
		this.#dragStartY = clientY;
		this.#startBrightness = this.#brightness;

		// Scale up for visual feedback
		if (this.#sphere) {
			this.#sphere.scale.setScalar(1.15);
		}

		this.#onInteractionStart?.();
	}

	/**
	 * Update brightness during drag
	 */
	private updateDrag (clientY: number): void {
		if (!this.#isDragging) return;

		// Vertical drag: up = brighter, down = dimmer
		const deltaY = this.#dragStartY - clientY;
		const deltaB = deltaY * this.#config.dragSensitivity;
		this.#brightness = Math.max(0, Math.min(1, this.#startBrightness + deltaB));

		this.applyBrightness();
		this.updateVisuals();
	}

	/**
	 * End drag and confirm brightness
	 */
	private endDrag (): void {
		if (!this.#isDragging) return;

		this.#isDragging = false;

		// Restore scale
		if (this.#sphere) {
			this.#sphere.scale.setScalar(1.0);
		}

		this.#onInteractionEnd?.();
	}

	/**
	 * Cancel drag and revert brightness
	 */
	private cancelDrag (): void {
		if (!this.#isDragging) return;

		this.#brightness = this.#startBrightness;
		this.applyBrightness();
		this.updateVisuals();
		this.endDrag();
	}

	// ── Event handlers ──────────────────────────────────────────────────

	private handleMouseDown = (event: MouseEvent): void => {
		if (event.button !== 0) return;

		this.updatePointer(event.clientX, event.clientY);
		if (this.checkHit()) {
			event.preventDefault();
			event.stopPropagation();
			this.startDrag(event.clientY);
		}
	};

	private handleMouseMove = (event: MouseEvent): void => {
		if (!this.#isDragging) return;
		event.preventDefault();
		this.updateDrag(event.clientY);
	};

	private handleMouseUp = (_event: MouseEvent): void => {
		this.endDrag();
	};

	private handleTouchStart = (event: TouchEvent): void => {
		if (event.touches.length !== 1) return;

		const touch = event.touches[0];
		this.updatePointer(touch.clientX, touch.clientY);
		if (this.checkHit()) {
			event.preventDefault();
			this.startDrag(touch.clientY);
		}
	};

	private handleTouchMove = (event: TouchEvent): void => {
		if (!this.#isDragging || event.touches.length !== 1) return;
		event.preventDefault();
		this.updateDrag(event.touches[0].clientY);
	};

	private handleTouchEnd = (_event: TouchEvent): void => {
		this.endDrag();
	};

	/**
	 * Bind DOM event listeners
	 */
	private bindEvents (): void {
		this.#domElement.addEventListener('mousedown', this.handleMouseDown, true);
		this.#domElement.addEventListener('mousemove', this.handleMouseMove, true);
		this.#domElement.addEventListener('mouseup', this.handleMouseUp, true);
		this.#domElement.addEventListener('mouseleave', this.handleMouseUp, true);

		this.#domElement.addEventListener('touchstart', this.handleTouchStart, { passive: false, capture: true });
		this.#domElement.addEventListener('touchmove', this.handleTouchMove, { passive: false, capture: true });
		this.#domElement.addEventListener('touchend', this.handleTouchEnd, true);
		this.#domElement.addEventListener('touchcancel', this.handleTouchEnd, true);
	}

	/**
	 * Unbind DOM event listeners
	 */
	private unbindEvents (): void {
		this.#domElement.removeEventListener('mousedown', this.handleMouseDown, true);
		this.#domElement.removeEventListener('mousemove', this.handleMouseMove, true);
		this.#domElement.removeEventListener('mouseup', this.handleMouseUp, true);
		this.#domElement.removeEventListener('mouseleave', this.handleMouseUp, true);

		this.#domElement.removeEventListener('touchstart', this.handleTouchStart, true as any);
		this.#domElement.removeEventListener('touchmove', this.handleTouchMove, true as any);
		this.#domElement.removeEventListener('touchend', this.handleTouchEnd, true);
		this.#domElement.removeEventListener('touchcancel', this.handleTouchEnd, true);
	}

	/**
	 * Called each frame from the render loop to keep the ring billboard-facing
	 * the camera. Call this from ThreeSceneEngine.onFrame().
	 */
	update (): void {
		if (!this.#isVisible || !this.#ring) return;
		this.#ring.lookAt(this.#camera.position);
	}
}
